import { brand } from 'config';

const _getPrimaryImagePath = (productInfo) => productInfo.imageURL ?? '';

const _getIdentifier = (productInfo) => {
  let productUrl = '';
  if (productInfo.id && productInfo.name) {
    const slug = productInfo.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(?:^-+)|(?:-+$)/g, '');
    productUrl = `/shop/product/${slug}?ID=${productInfo.id}`;
  }
  return { productUrl };
};

const _getDetailBrand = (productInfo) => {
  if (typeof productInfo.brand?.name === 'string') return productInfo.brand.name;
  if (typeof productInfo.brand === 'string') return productInfo.brand;
  return '';
};

const _getDetailFlags = (productInfo) => ({
  isNew: productInfo.isNew ?? false,
});

const _getColorAndSizeArray = (upcInfo) => {
  const parts = [];
  if (upcInfo.size) parts.push(upcInfo.size);
  if (upcInfo.color) parts.push(upcInfo.color);
  if (upcInfo.type) parts.push(upcInfo.type);
  return parts.length > 0 ? [parts.join(', ')] : [];
};

const _getDetail = (productInfo, upcInfo) => ({
  name: productInfo.name ?? '',
  brand: _getDetailBrand(productInfo),
  flags: _getDetailFlags(productInfo),
  upcId: upcInfo.id ?? 0,
  colorAndSize: _getColorAndSizeArray(upcInfo),
  quantity: 1,
});

const _getImagery = (productInfo, primaryImagePath) => ({
  alt: productInfo.name ?? '',
  primaryImage: {
    filePath: primaryImagePath,
  },
});

const _getTraits = (upcInfo, primaryImagePath) => ({
  colors: {
    selectedColor: {
      id: upcInfo.id ?? 0,
      name: upcInfo.color ?? '',
      normalName: upcInfo.color ?? '',
      imagery: {
        primaryImage: {
          filePath: primaryImagePath,
        },
      },
    },
  },
});

const _getTieredPrice = (priceDetailsInput) => {
  const priceDetails = priceDetailsInput || {};
  const tieredPrice = [];

  if (priceDetails.onSale) {
    tieredPrice.push({
      label: `${priceDetails.originalPriceLabel ?? 'Regular Price'} [PRICE]`,
      values: [
        {
          value: (priceDetails.originalPrice ?? 0).toFixed(2),
          formattedValue: `$${(priceDetails.originalPrice ?? 0).toFixed(2)}`,
          type: 'regular',
        },
      ],
    });
    tieredPrice.push({
      label: `${priceDetails.retailPriceLabel ?? 'Sale Price'} [PRICE]`,
      values: [
        {
          value: (priceDetails.retailPrice ?? 0).toFixed(2),
          formattedValue: `$${(priceDetails.retailPrice ?? 0).toFixed(2)}`,
          type: 'discount',
          percentOff: priceDetails.percentageOff ? [priceDetails.percentageOff] : '',
        },
      ],
    });
  } else {
    const currentPrice = priceDetails.retailPrice ?? priceDetails.originalPrice;
    tieredPrice.push({
      label: `${priceDetails.originalPriceLabel ?? 'Price'} [PRICE]`,
      values: [
        {
          value: (currentPrice ?? 0).toFixed(2),
          formattedValue: `$${(currentPrice ?? 0).toFixed(2)}`,
          type: 'regular',
        },
      ],
    });
  }
  return tieredPrice;
};

const _getBadges = (item) => {
  const isBcom = brand === 'bcom';
  const badges = [];
  const upcInfo = item?.upc || {};
  const productInfoForPrice = item.upc?.product || item.product || {};
  const priceTypeTextValue = upcInfo.price?.priceTypeText || productInfoForPrice.price?.priceTypeText;

  // Create a badge from priceTypeText if it exists, for BCOM only.
  // For MCOM, this is handled by specialOffersBadge to avoid duplication.
  if (isBcom && priceTypeTextValue) {
    const badge = {
      header: priceTypeTextValue,
      showSelectColors: false,
    };
    // Description is only needed for BCOM for the tooltip.
    badge.description = priceTypeTextValue;
    badges.push(badge);
  }
  // Create badges from promotions
  if (item?.promotions?.length > 0) {
    item.promotions.forEach((promo) => {
      const promoHeader = promo.badgeTextAttributeValue ?? '';
      const badge = {
        header: promoHeader,
        showSelectColors: false,
      };
      // Description is only needed for BCOM for the tooltip.
      if (isBcom) {
        badge.description = promoHeader;
      }
      badges.push(badge);
    });
  }
  return badges;
};

const _getPricing = (upcInfo, item) => ({
  price: {
    priceType: {
      applicableToAllUpcs: true,
      text: upcInfo.price?.priceTypeText ?? '',
      onSale: upcInfo.price?.onSale ?? false,
    },
    tieredPrice: _getTieredPrice(upcInfo.price),
  },
  badges: _getBadges(item),
});

const _getMkpLineItemDetails = (productInfo) => {
  const details = {};
  if (productInfo.marketPlaceFlag && productInfo.seller?.sellerName) {
    details.sellerName = productInfo.seller.sellerName;
  }
  return details;
};

const _getMultipleUpcStatus = (productInfo) => !!productInfo.multipleUpc;

const _getCheckStockThroughPhoneOnlyStatus = (productInfo) => !!productInfo.phoneOnly;

/**
 * Transforms a single item from customerLists.json format to product.json format.
 * @param {object} item - A single item object from the customerLists.json items array.
 * @returns {object} - The transformed product object.
 */

export const mapProductData = (item) => {
  if (!item) {
    return {};
  }

  const productInfo = item.product || item.upc?.product || {};
  const upcInfo = item?.upc || {};
  const primaryImagePath = _getPrimaryImagePath(productInfo);
  const transformedProduct = {
    id: item.itemGuid ?? 0,
    productId: productInfo.id ?? 0,
    identifier: _getIdentifier(productInfo),
    detail: _getDetail(productInfo, upcInfo),
    imagery: _getImagery(productInfo, primaryImagePath),
    traits: _getTraits(upcInfo, primaryImagePath),
    pricing: _getPricing(upcInfo, item),
    mkpLineItemDetails: _getMkpLineItemDetails(productInfo),
    hasMultipleUpc: _getMultipleUpcStatus(productInfo),
    checkStockThroughPhoneOnly: _getCheckStockThroughPhoneOnlyStatus(productInfo),
  };

  return transformedProduct;
};

/**
 * Determines the availability of a product item.
 * It checks availability first from the UPC information, then from the product information,
 * defaulting to false if not found.
 * @param {object} item - A single item object, typically from a list.
 * @returns {boolean} - True if the item is available, false otherwise.
 */

export const getAvailable = (item) => {
  if (!item) {
    return false;
  }
  const productInfo = item.upc?.product || item.product || {};
  const upcInfo = item?.upc || {};
  return upcInfo?.availability?.available ?? productInfo?.available ?? false;
};

export default {
  mapProductData,
  getAvailable,
};
